import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { notificationService } from '../services/notificationService';
import RelatedInventory from '../components/RelatedInventory';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

const MONTHS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1).padStart(2, '0'),
  label: String(i + 1).padStart(2, '0')
}));

const DAYS = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1).padStart(2, '0'),
  label: String(i + 1).padStart(2, '0')
}));

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 80 }, (_, i) => ({
  value: String(currentYear - i),
  label: String(currentYear - i)
}));

const SUFFIXES = ['', 'Jr', 'Sr', 'II', 'III', 'IV'];

// Year options for vehicle dropdowns
const VEHICLE_YEARS = Array.from({ length: 30 }, (_, i) => ({
  value: String(currentYear + 1 - i),
  label: String(currentYear + 1 - i)
}));

const PreApproval = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    middleInitial: '',
    lastName: '',
    suffix: '',
    ssn: '',
    dobMonth: '',
    dobDay: '',
    dobYear: '',
    address: '',
    zip: '',
    city: '',
    state: '',
    homePhone: '',
    cellPhone: '',
    email: '',
    // Desired Vehicle
    desiredYear: '',
    desiredMake: '',
    desiredModel: '',
    stockNumber: '',
    financeAmount: '',
    // Trade-In Vehicle
    tradeYear: '',
    tradeMake: '',
    tradeModel: '',
    // Consent
    iAgree: false,
    signatureName: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.iAgree || !formData.signatureName) return;

    setLoading(true);
    try {
      const insertData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email || null,
        phone: formData.cellPhone || formData.homePhone || null,
        date_of_birth: formData.dobYear && formData.dobMonth && formData.dobDay
          ? `${formData.dobYear}-${formData.dobMonth}-${formData.dobDay}`
          : null,
        ssn: formData.ssn,
        street_address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip,
        vehicle_interest: formData.desiredYear && formData.desiredMake
          ? `${formData.desiredYear} ${formData.desiredMake} ${formData.desiredModel}`.trim()
          : null,
        credit_check_consent: true,
        terms_accepted: true,
        status: 'pending',
        created_at: new Date().toISOString()
      };

      console.log('Submitting pre-approval:', insertData);
      const { error } = await supabase
        .from('finance_applications')
        .insert([insertData]);

      if (error) {
        console.error('Supabase error details:', error.message, error.details, error.hint);
        throw error;
      }
      setSubmitted(true);

      // Fire Meta Pixel Lead event
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: 'pre_approval', content_category: 'finance_application' });
      }

      // Send email notification to sales team
      try {
        await notificationService.sendFinanceApplicationNotification({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.cellPhone || formData.homePhone,
          monthlyIncome: 'See admin panel',
          employerName: 'See admin panel',
          downPayment: formData.financeAmount || '0',
          ssn: formData.ssn ? `***-**-${formData.ssn.slice(-4)}` : 'Not provided'
        });
      } catch (emailErr) {
        console.warn('Pre-approval email notification failed:', emailErr);
      }

      // Redirect to submission success page for Meta conversion tracking
      navigate('/submission-success?type=pre-approval');
    } catch (err) {
      console.error('Pre-approval submission error:', err);
      alert(`Something went wrong: ${err.message || 'Unknown error'}. Please call us at (817) 382-2316.`);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-amber-500 focus:border-amber-400 focus:outline-none text-sm";
  const selectClass = "w-full px-3 py-2.5 border border-gray-300 rounded focus:ring-2 focus:ring-amber-500 focus:border-amber-400 focus:outline-none text-sm bg-white";
  const labelClass = "text-sm font-medium text-gray-700";
  const requiredStar = <span className="text-red-500 ml-0.5">*</span>;

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 lg:pt-32 flex items-center justify-center">
        <Helmet>
          <title>Pre-Approval Submitted | Paradise-Performance</title>
        </Helmet>
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-lg shadow-lg p-12 text-center max-w-lg"
        >
          <div className="text-green-500 text-6xl mb-4">✓</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Pre-Approval Submitted</h2>
          <p className="text-gray-600 mb-6">
            We've received your information and will be in touch shortly with your pre-approval status.
          </p>
          <Link
            to="/inventory"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Browse Inventory
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 lg:pt-32">
      <Helmet>
        <title>Get Pre-Approved for Auto Financing | Paradise-Performance</title>
        <meta name="description" content="Get pre-approved for your next vehicle purchase with a soft credit inquiry. No impact to your credit score. Quick results from Paradise-Performance." />
        <link rel="canonical" href="https://paradise-performance.com/pre-approval" />
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white p-6 rounded-t-lg">
            <h1 className="text-2xl font-bold mb-1">Pre-Approval Application</h1>
            <p className="text-amber-100 text-sm">Soft inquiry — no impact to your credit score</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 md:p-8">
            {/* Required Fields Note */}
            <p className="text-xs text-red-500 mb-6">{requiredStar} required fields</p>

            {/* ─── Personal Information ─── */}
            <h2 className="text-lg font-bold text-gray-900 mb-4">Personal Information</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div>
                <input
                  type="text"
                  placeholder="First Name *"
                  required
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <input
                  type="text"
                  placeholder="MI"
                  maxLength={1}
                  value={formData.middleInitial}
                  onChange={(e) => handleChange('middleInitial', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Last Name *"
                  required
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <select
                  value={formData.suffix}
                  onChange={(e) => handleChange('suffix', e.target.value)}
                  className={selectClass}
                >
                  <option value="">Suffix</option>
                  {SUFFIXES.filter(Boolean).map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* SSN & DOB Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div>
                <input
                  type="text"
                  placeholder="SSN *"
                  required
                  value={formData.ssn}
                  onChange={(e) => handleChange('ssn', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 whitespace-nowrap">Date of Birth</span>
                <select
                  value={formData.dobMonth}
                  onChange={(e) => handleChange('dobMonth', e.target.value)}
                  className={selectClass}
                >
                  <option value="">MM</option>
                  {MONTHS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <select
                  value={formData.dobDay}
                  onChange={(e) => handleChange('dobDay', e.target.value)}
                  className={selectClass}
                >
                  <option value="">DD</option>
                  {DAYS.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
                <select
                  value={formData.dobYear}
                  onChange={(e) => handleChange('dobYear', e.target.value)}
                  className={selectClass}
                >
                  <option value="">YYYY</option>
                  {YEARS.map(y => (
                    <option key={y.value} value={y.value}>{y.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* ─── Residential Information ─── */}
            <h2 className="text-lg font-bold text-gray-900 mb-4 mt-8">Residential Information</h2>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Physical Address *"
                required
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div>
                <input
                  type="text"
                  placeholder="Zip *"
                  required
                  value={formData.zip}
                  onChange={(e) => handleChange('zip', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <input
                  type="text"
                  placeholder="City *"
                  required
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <select
                  required
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  className={selectClass}
                >
                  <option value="">State *</option>
                  {US_STATES.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Phone & Email */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div>
                <input
                  type="tel"
                  placeholder="Home Phone"
                  value={formData.homePhone}
                  onChange={(e) => handleChange('homePhone', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <input
                  type="tel"
                  placeholder="Cell Phone Number"
                  value={formData.cellPhone}
                  onChange={(e) => handleChange('cellPhone', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <input
                  type="email"
                  placeholder="E-Mail Address"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            {/* ─── Vehicle Information ─── */}
            <h2 className="text-lg font-bold text-gray-900 mb-4 mt-8">Vehicle Information</h2>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 mb-4">
              {/* Desired Vehicle Column */}
              <div>
                <h3 className="font-semibold text-sm text-gray-800 mb-2">Desired Vehicle</h3>
                <div className="space-y-3">
                  <select
                    value={formData.desiredYear}
                    onChange={(e) => handleChange('desiredYear', e.target.value)}
                    className={selectClass}
                  >
                    <option value="">Year</option>
                    {VEHICLE_YEARS.map(y => (
                      <option key={y.value} value={y.value}>{y.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Make"
                    value={formData.desiredMake}
                    onChange={(e) => handleChange('desiredMake', e.target.value)}
                    className={inputClass}
                  />
                  <input
                    type="text"
                    placeholder="Model"
                    value={formData.desiredModel}
                    onChange={(e) => handleChange('desiredModel', e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Trade-In Vehicle Column */}
              <div>
                <h3 className="font-semibold text-sm text-gray-800 mb-2">Trade-In Vehicle</h3>
                <div className="space-y-3">
                  <select
                    value={formData.tradeYear}
                    onChange={(e) => handleChange('tradeYear', e.target.value)}
                    className={selectClass}
                  >
                    <option value="">Year</option>
                    {VEHICLE_YEARS.map(y => (
                      <option key={y.value} value={y.value}>{y.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Make"
                    value={formData.tradeMake}
                    onChange={(e) => handleChange('tradeMake', e.target.value)}
                    className={inputClass}
                  />
                  <input
                    type="text"
                    placeholder="Model"
                    value={formData.tradeModel}
                    onChange={(e) => handleChange('tradeModel', e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            {/* Stock Number & Finance Amount */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              <div>
                <input
                  type="text"
                  placeholder="Stock Number"
                  value={formData.stockNumber}
                  onChange={(e) => handleChange('stockNumber', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <input
                  type="text"
                  placeholder="Finance Amount"
                  value={formData.financeAmount}
                  onChange={(e) => handleChange('financeAmount', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            {/* ─── FCRA Disclosure & Consent ─── */}
            <div className="border-t border-gray-200 pt-6 mb-6">
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                By clicking on the <strong>I Agree</strong> checkbox, you are confirming that you have read and understand the{' '}
                <a
                  href="https://www.700creditsolution.com/PrivacyPolicy.aspx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Privacy Notice
                </a>{' '}
                and{' '}
                <a
                  href="https://www.700creditsolution.com/FCRA.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Electronic Signature Disclosure
                </a>
                , your name as entered in the Electronic Signature is accurate, and you are authorizing{' '}
                <strong>Paradise-Performance</strong> under all applicable federal and state laws, including the Fair Credit Reporting Act, to obtain information from your personal credit profile. Also, you are confirming you understand that any pre-approval is subject to review and acceptance of credit information.
              </p>

              <div className="text-xs text-gray-500 mb-4 space-y-1">
                <p>
                  <a href="https://www.700creditsolution.com/FCRA.pdf" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                    Fair Credit Reporting Act (FCRA) Disclosure
                  </a>
                </p>
                <p>
                  <a href="https://www.700creditsolution.com/SUMMARY_OF_YOUR_RIGHTS_UNDER_FCRA.pdf" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                    Summary of Your Rights Under FCRA
                  </a>
                </p>
                <p>
                  <a href="https://www.700creditsolution.com/consumerdispute.pdf" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                    Consumer Dispute Information
                  </a>
                </p>
                <p>
                  <a href="https://www.700creditsolution.com/PrivacyPolicy.aspx" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                    700Credit Privacy Policy
                  </a>
                </p>
              </div>

              {/* I Agree Checkbox */}
              <div className="flex items-center gap-3 mb-4">
                <input
                  type="checkbox"
                  id="iAgree"
                  checked={formData.iAgree}
                  onChange={(e) => handleChange('iAgree', e.target.checked)}
                  className="w-4 h-4 text-amber-600 rounded"
                  required
                />
                <label htmlFor="iAgree" className="text-sm font-medium text-gray-700">
                  I Agree
                </label>
                <input
                  type="text"
                  placeholder="First, MI and Last Name must match exactly as entered above *"
                  required
                  value={formData.signatureName}
                  onChange={(e) => handleChange('signatureName', e.target.value)}
                  className={`flex-1 ${inputClass}`}
                />
              </div>
            </div>

            {/* Submit */}
            <div className="text-center">
              <button
                type="submit"
                disabled={loading || !formData.iAgree || !formData.signatureName}
                className="px-12 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 disabled:bg-gray-400 transition-colors text-lg shadow-lg"
              >
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </motion.div>
      </div>

      {/* Related Inventory */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <RelatedInventory limit={4} title="Browse Our Inventory" />
      </div>
    </div>
  );
};

export default PreApproval;
